import React, { useState, useEffect } from "react";
import { Fab, Tooltip, Zoom, Fade } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { styled, keyframes } from "@mui/material/styles";

// Define multiple animations
const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const blink = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const slideIn = keyframes`
  0% {
    transform: translateX(100px) scale(0.5);
    opacity: 0;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const shake = keyframes`
  0%, 100% {transform: rotate(0deg);}
  25% {transform: rotate(10deg);}
  50% {transform: rotate(-10deg);}
  75% {transform: rotate(5deg);}
`;

// Styled component with multiple animation options
const WhatsAppFab = styled(Fab, {
  shouldForwardProp: (prop) => !["animate", "animationType"].includes(prop),
})(({ animate, animationType }) => ({
  position: "fixed",
  bottom: 20,
  right: 20,
  backgroundColor: "#25D366",
  color: "white",
  "&:hover": {
    backgroundColor: "#128C7E",
    transform: "scale(1.1)",
  },
  zIndex: 1000,
  transition: "all 0.3s ease-in-out",
  animation: animate
    ? {
        pulse: `${pulse} 2s infinite`,
        bounce: `${bounce} 2s infinite`,
        blink: `${blink} 1.5s infinite`,
        shake: `${shake} 0.8s 3`,
        slide: `${slideIn} 0.5s ease-out`,
      }[animationType]
    : "none",

  // Special shadow effect
  boxShadow:
    "0 4px 20px rgba(37, 211, 102, 0.4), 0 0 0 1px rgba(37, 211, 102, 0.1)",

  // Mobile responsiveness
  "@media (max-width: 600px)": {
    bottom: 16,
    right: 16,
    width: 60,
    height: 60,
  },
}));

// Floating notification badge
const NotificationBadge = styled("div")({
  position: "absolute",
  top: -5,
  right: -5,
  backgroundColor: "#FF4081",
  color: "white",
  borderRadius: "50%",
  width: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "bold",
  animation: `${pulse} 1.5s infinite`,
  boxShadow: "0 2px 8px rgba(255, 64, 129, 0.4)",
});

// Container for floating effect
const FloatingContainer = styled("div")({
  position: "relative",
  display: "inline-block",
});

const WhatsAppButton = () => {
  const [animationType, setAnimationType] = useState("pulse");
  const [showTooltip, setShowTooltip] = useState(true);
  const [hasNotification, setHasNotification] = useState(true);

  const phoneNumber = "917019854530";
  const message = "Hello! I'm interested in your caregiver services.";
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  // Cycle through different animations
  useEffect(() => {
    const animationTypes = ["pulse", "bounce", "shake"];
    let currentIndex = 0;

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % animationTypes.length;
      setAnimationType(animationTypes[currentIndex]);

      // Briefly show tooltip every few animations
      setShowTooltip(false);
      setTimeout(() => setShowTooltip(true), 100);
    }, 5000); // Change animation every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Remove notification after 15 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasNotification(false);
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  // Initial slide-in animation
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    // Delay the button appearance for dramatic effect
    const timer = setTimeout(() => {
      setShowButton(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (!showButton) {
    return null; // Don't render until after delay
  }

  return (
    <FloatingContainer>
      {showTooltip && (
        <Tooltip
          title="Chat with us on WhatsApp!"
          placement="left"
          arrow
          TransitionComponent={Zoom}
        >
          <WhatsAppFab
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat on WhatsApp"
            size="large"
            animate={true}
            animationType={animationType}
            onMouseEnter={() => setAnimationType("shake")}
            onMouseLeave={() => setAnimationType("pulse")}
            onClick={() => {
              // Track click if needed
              console.log("WhatsApp button clicked");
              setHasNotification(false);
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 32 }} />
            {hasNotification && <NotificationBadge>!</NotificationBadge>}
          </WhatsAppFab>
        </Tooltip>
      )}

      {/* Optional text bubble */}
      <Fade in={hasNotification} timeout={1000}>
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            right: "10px",
            backgroundColor: "white",
            color: "#333",
            padding: "10px 15px",
            borderRadius: "20px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxWidth: "200px",
            fontSize: "14px",
            fontWeight: "500",
            animation: `${bounce} 2s infinite`,
            display: { xs: "none", sm: "block" },
          }}
        >
          💬 Click to chat with us!
          <div
            style={{
              position: "absolute",
              bottom: "-8px",
              right: "20px",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "10px solid white",
            }}
          />
        </div>
      </Fade>
    </FloatingContainer>
  );
};

export default WhatsAppButton;
